import React from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import cookie from 'js-cookie';
import queryString from 'query-string';

import { authData, userIsLoggedIn } from 'redux/User';
import { LATEST_PPO_VERSION } from 'screens/PPOMonarch/PostPurchase/constants';

import analytics from 'services/analytics';
import { bugsnagError } from 'services/bugsnag';
import {
  navigateToHomePage,
  navigateToProjectThankYouFor,
  navigateToDirectPackagePurchase,
  navigateToProjectPageFor,
  navigateToRenovationPackages
} from 'services/routing';
import warehouse from 'services/warehouse';

import { CheckoutActions } from 'services/Data';

import ModsyHeartLoader from 'shared/components/ModsyHeartLoader';

import ErrorPage from 'ts/studio/components/error-page';
import {
  HAS_COMPLETED_STYLE_QUIZ,
  IS_REGISTERED_FROM_BLOG,
  MODSY_RENO_DISABLED,
  RENO_MULTIROOM_SKUS,
  SHOWROOM_PACKAGE_SKU
} from 'utils/constants';

import DirectRouter from './DirectRouter';

export class DirectRouterController extends React.Component {
  state = {
    selectedPackage: null,
    space: null,
    spaceRequested: false,
    unrecoverableError: false
  };

  async componentDidMount () {
    try {
      // Required: get packageSku from URL query params
      const { location } = this.props;
      const { packageSku } = queryString.parse(location.search);
      if (!packageSku) {
        bugsnagError(new Error(`cannot find packageSku in url params`));
        this.setState({ unrecoverableError: true });
        return;
      }

      // Required: get the package with associated packageSku
      const packages = await warehouse.fetchPackages();
      let selectedPackage = packages.find(p => p.sku === packageSku);
      if (!selectedPackage) {
        bugsnagError(new Error(`cannot find package with sku ${packageSku}`));
        this.setState({ unrecoverableError: true });
        return;
      }
      selectedPackage.packageSku = packageSku;

      if (MODSY_RENO_DISABLED.includes(selectedPackage.sku)) {
        navigateToRenovationPackages();
        return;
      }

      this.setState({ selectedPackage });
    } catch (error) {
      bugsnagError(error);
      this.setState({ unrecoverableError: true });
    }
  }

  async componentDidUpdate () {
    const { userIsLoggedIn, location } = this.props;
    const { selectedPackage, spaceRequested } = this.state;
    const { packageSku } = queryString.parse(location.search);

    // if the packageSku changes, get the new package
    if (selectedPackage && selectedPackage.packageSku && packageSku && selectedPackage.packageSku !== packageSku) {
      const packages = await warehouse.fetchPackages();
      let selectedPackage = packages.find(p => p.sku === packageSku);
      if (!selectedPackage) {
        bugsnagError(new Error(`cannot find package with sku ${packageSku}`));
        this.setState({ unrecoverableError: true });
        return;
      }
      selectedPackage.packageSku = packageSku;

      if (MODSY_RENO_DISABLED.includes(selectedPackage.sku)) {
        navigateToRenovationPackages();
        return;
      }

      this.setState({ selectedPackage });
    }

    // When both the user is logged in and we have identified
    // the package, go ahead and create the space. Make sure
    // we only do this once.
    if (userIsLoggedIn && selectedPackage && !spaceRequested) {
      this.setState({ spaceRequested: true }, () => {
        this.handleRequestCreateSpace();
      });
    }
  }

  render () {
    return (
      <React.Fragment>
        <Helmet
          meta={[
            {
              name: 'robots',
              content: 'noindex'
            }
          ]}
        />
        {this.renderContent()}
      </React.Fragment>
    );
  }

  renderContent = () => {
    const { authData, userIsLoggedIn } = this.props;
    const { selectedPackage, space, unrecoverableError } = this.state;

    if (unrecoverableError) {
      return <ErrorPage />;
    }

    if (
      !authData ||
      authData.loading ||
      !selectedPackage ||
      (userIsLoggedIn && !space)
    ) {
      return <ModsyHeartLoader scale={1} />;
    }

    return <DirectRouter
      {...this.props}
      handlePaymentBackClick={this.handlePaymentBackClick}
      handlePaymentSuccess={this.handlePaymentSuccess}
      selectedPackage={this.state.selectedPackage}
      space={this.state.space}
      onPackageChange={this.handlePackageChange}
    />;
  }

  handleRequestCreateSpace = async () => {
    try {
      const { selectedPackage } = this.state;

      let space = null;
      if (selectedPackage.packageSku === SHOWROOM_PACKAGE_SKU) {
        const { spaceId, designId, styleboardInfoId } = await warehouse.createShowroom();
        space = {
          designId,
          id: styleboardInfoId, // reqd for SimpleCheckout
          spaceId,
          styleboardInfoId
        };
      } else {
        let spaceInfo = {
          additional_storage_needed: false,
          additional_seating_needed: false,
          budget_max: 2500,
          budget_min: 0,
          kid_friendly: false,
          multi_purpose_room: false,
          other_brand: '',
          pet_friendly: false,
          project_description: '',
          project_scope: 0,
          referral_source: 'direct', // this is necessary to inform PPO that step 1 is not complete for this space
          room_redesign_reason: '',
          style_preferences: '',
          tv_viewing_required: false,
          ppo_version: LATEST_PPO_VERSION
        };

        space = await warehouse.createSpace(spaceInfo);
        if (space) { space.spaceId = space.space_id; }
      }

      this.setState({ space });
    } catch (error) {
      bugsnagError(error);
      this.setState({ unrecoverableError: true });
    }
  };

  handlePaymentBackClick = () => {
    navigateToHomePage({ search: '' });
  };

  handlePaymentSuccess = ({ email, itemName, orderInfo, orderNumber, orderTotal }) => {
    const { selectedPackage } = this.state;
    const { packageSku } = selectedPackage || {};
    const {
      authData,
      initializeModsyFBPixel,
      packagePurchased,
      retailPartner,
      setCheckoutPackageName,
      trackEvent,
      userIsLoggedIn
    } = this.props;
    const { space } = this.state;

    // Analytics
    const userInfo = {
      em: email,
      fn: orderInfo.nameOnCard.substr(0, orderInfo.nameOnCard.indexOf(' ')).toLowerCase(),
      ln: orderInfo.nameOnCard.substr(orderInfo.nameOnCard.indexOf(' ') + 1).toLowerCase(),
      zp: orderInfo.billingZip
    };

    const packagePurchasedInfo = {
      itemName,
      orderTotal,
      retailPartner,
      orderInfo,
      orderNumber,
      email,
      spaceId: space && space.spaceId,
      direct: true,
      userId: userIsLoggedIn ? authData.userId : null
    };

    packagePurchased(packagePurchasedInfo);

    // reinitialize pixel with extra user information
    initializeModsyFBPixel(userInfo);

    trackEvent({ event: 'Order Success',
      data: {
        orderNum:
        orderNumber,
        total: orderTotal,
        direct: true
      }
    });

    // Set Package Name to Redux store
    setCheckoutPackageName(itemName);

    cookie.remove(HAS_COMPLETED_STYLE_QUIZ);
    cookie.remove(IS_REGISTERED_FROM_BLOG);

    const isRenoPackage = RENO_MULTIROOM_SKUS.includes(packageSku);

    if (isRenoPackage) {
      navigateToProjectPageFor({spaceId: space.spaceId});
    } else {
      navigateToProjectThankYouFor({spaceId: space.spaceId});
    }
  };

  handlePackageChange = ({ packageSku }) => {
    // replace the url parameter with the new sku
    navigateToDirectPackagePurchase(packageSku);
  }
}

const mapStateToProps = (state) => {
  return {
    authData: authData(state),
    userIsLoggedIn: userIsLoggedIn(state),
    retailPartner: state.retailPartner && state.retailPartner.slug
  };
};

const mapDispatchToProps = (dispatch) => ({
  trackEvent: ({ event, data }) => dispatch(analytics.trackEvent({ event, data })),
  initializeModsyFBPixel: (userInfo) => dispatch(analytics.initializeModsyFBPixel(userInfo)),
  packagePurchased: (packagePurchasedInfo) => dispatch(analytics.packagePurchased(packagePurchasedInfo)),
  setCheckoutPackageName: (name) => dispatch(CheckoutActions.setCheckoutPackageName(name))
});

export default connect(mapStateToProps, mapDispatchToProps)(DirectRouterController);
